import { createSelector } from "@reduxjs/toolkit";
import objectHash from "object-hash";
import type {
  FinancialStatementConfiguration,
  FinancialStatementGeneralSettings,
  ReportField,
  StatementDataColumnConfiguration,
} from "../../../../../../api/types/svTypes.ts";
import { canBeReferencedInCustomFormula, isCustomColumn } from "../../utilities/fieldsHelper.ts";
import { getTabularFieldGridId } from "../../utilities/getTabularFieldGridId.ts";
import {
  isChartReportType,
  isFinancialStatementReportType,
  isListReportType,
  isPivotReportType,
  usesTabularConfiguration,
  isTileReportType,
} from "../../utilities/reportTypeHelper.ts";
import type {
  ChartFieldState,
  ListFieldState,
  PivotFieldState,
  StatementAxisState,
  StatementLineState,
  TabularFieldState,
  TileFieldState,
} from "../types/fieldsState.types.ts";
import type { RootState } from "../types/store.types.ts";
import { getTabularColumnAlignments } from "../../components/viewer/tabular/utils/tabularColumnAlignment.ts";

export const selectTabularFieldsState = (state: RootState): TabularFieldState => {
  return state.fieldsState.tabularFieldsReducer;
};

export const selectTabularSettingsState = (state: RootState) => {
  return state.fieldsState.tabularFieldsReducer.settings;
};

export const selectPivotSettingsState = (state: RootState) => {
  return state.fieldsState.pivotFieldsReducer.settings;
};

export const selectChartSettingsState = (state: RootState) => {
  return state.fieldsState.chartFieldsReducer.settings;
};

export const selectTileSettingsState = (state: RootState) => {
  return state.fieldsState.tileFieldsReducer.settings;
};

export const selectPivotFieldsState = (state: RootState): PivotFieldState => {
  return state.fieldsState.pivotFieldsReducer;
};

export const selectChartFieldsState = (state: RootState): ChartFieldState => {
  return state.fieldsState.chartFieldsReducer;
};

export const selectTileFieldsState = (state: RootState): TileFieldState => {
  return state.fieldsState.tileFieldsReducer;
};

export const selectListFieldsState = (state: RootState): ListFieldState => {
  return state.fieldsState.listFieldsReducer;
};

export const selectListSettingsState = (state: RootState) => {
  return state.fieldsState.listFieldsReducer.settings;
};

export const selectStatementLines = (state: RootState): StatementLineState[] =>
  state.fieldsState.financialStatementFieldsReducer.lines;

export const selectStatementAxes = (state: RootState): StatementAxisState[] =>
  state.fieldsState.financialStatementFieldsReducer.axes;

export const selectStatementDataColumns = (state: RootState): StatementDataColumnConfiguration[] =>
  state.fieldsState.financialStatementFieldsReducer.dataColumns;

export const selectSelectedStatementLineId = (state: RootState): string | undefined =>
  state.fieldsState.financialStatementFieldsReducer.selectedLineId;

export const selectFinancialStatementSettings = (state: RootState): FinancialStatementGeneralSettings =>
  state.fieldsState.financialStatementFieldsReducer.settings;

export const selectFinancialStatementConfiguration = (state: RootState): FinancialStatementConfiguration | undefined =>
  state.fieldsState.financialStatementFieldsReducer.configuration;

export const selectReportType = (state: RootState) => {
  return state.fieldsState.fieldsStateReducer.reportType;
};

export const selectTabularFields = (state: RootState) => {
  if (!usesTabularConfiguration(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.tabularFieldsReducer.fields;
};

export const selectTileFields = (state: RootState) => {
  if (!isTileReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.tileFieldsReducer.fields;
};

export const selectListFields = (state: RootState) => {
  if (!isListReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.listFieldsReducer.fields;
};

export const selectTabularColumnAlignments = createSelector(
  (state: RootState) => state.fieldsState.tabularFieldsReducer.fields,
  (state: RootState) => state.fieldsState.tabularFieldsReducer.grouping,
  (fields, grouping) =>
    getTabularColumnAlignments(
      fields.map((field) => field.config),
      grouping
    )
);

export const selectTabularGrouping = createSelector(
  (state: RootState) => state.fieldsState.fieldsStateReducer.reportType,
  (state: RootState) => state.fieldsState.tabularFieldsReducer.grouping,
  (reportType, grouping) => {
    if (!usesTabularConfiguration(reportType)) {
      throw new Error("Not supported");
    }
    return grouping;
  }
);

export const selectFieldsForCalculation = createSelector(
  (state: RootState) => state.fieldsState.fieldsStateReducer.reportType,
  (state: RootState) => state.fieldsState.tabularFieldsReducer.fields,
  (state: RootState) => state.fieldsState.pivotFieldsReducer.values,
  (state: RootState) => state.fieldsState.chartFieldsReducer.values,
  (state: RootState) => state.fieldsState.tileFieldsReducer.fields,
  (state: RootState) => state.fieldsState.listFieldsReducer.fields,
  (reportType, tabularFields, pivotValues, chartValues, tileFields, listFields) => {
    if (usesTabularConfiguration(reportType)) {
      return tabularFields.filter(canBeReferencedInCustomFormula);
    }
    if (isPivotReportType(reportType)) {
      return pivotValues.filter(canBeReferencedInCustomFormula);
    }
    if (isChartReportType(reportType)) {
      return chartValues.filter(canBeReferencedInCustomFormula);
    }
    if (isTileReportType(reportType)) {
      return tileFields.filter(canBeReferencedInCustomFormula);
    }
    if (isListReportType(reportType)) {
      return listFields.filter(canBeReferencedInCustomFormula);
    }
    if (isFinancialStatementReportType(reportType)) {
      return [];
    }
    throw new Error("Report type not supported");
  }
);

export const selectAggregationFields = (state: RootState) => {
  if (usesTabularConfiguration(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.tabularFieldsReducer.fields;
  }
  if (isPivotReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.pivotFieldsReducer.values;
  }
  if (isChartReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.chartFieldsReducer.values;
  }
  if (isTileReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.tileFieldsReducer.fields;
  }
  if (isListReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.listFieldsReducer.fields;
  }
  throw new Error("Report type not supported");
};

export const selectCustomFields = (state: RootState) => {
  if (usesTabularConfiguration(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.tabularFieldsReducer.fields.filter(isCustomColumn);
  }
  if (isPivotReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.pivotFieldsReducer.values.filter(isCustomColumn);
  }
  if (isChartReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.chartFieldsReducer.values.filter(isCustomColumn);
  }
  if (isTileReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.tileFieldsReducer.fields.filter(isCustomColumn);
  }
  if (isListReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.listFieldsReducer.fields.filter(isCustomColumn);
  }
  throw new Error("Report type not supported");
};

export const selectTabularFreezedColumns = createSelector(
  (state: RootState) => state.fieldsState.fieldsStateReducer.reportType,
  (state: RootState) => state.fieldsState.tabularFieldsReducer.settings.freezedColumns,
  (reportType, freezedColumns) => {
    if (!usesTabularConfiguration(reportType)) {
      throw new Error("Not supported");
    }
    return freezedColumns ?? [];
  }
);

export const selectFieldGridId = (state: RootState, field: ReportField) => {
  if (!usesTabularConfiguration(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return getTabularFieldGridId(
    field,
    state.fieldsState.tabularFieldsReducer.grouping,
    state.fieldsState.tabularFieldsReducer.settings.showGroupedColumns
  );
};

export const selectIsFieldFreezed = (state: RootState, field: ReportField) => {
  if (!usesTabularConfiguration(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return selectTabularFreezedColumns(state).includes(selectFieldGridId(state, field));
};

export const selectIsGroupedFields = (state: RootState, field: ReportField) => {
  if (!usesTabularConfiguration(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.tabularFieldsReducer.grouping.some((g) => g.id === field.config.guid);
};

export const selectTabularShowGroupedColumns = (state: RootState) => {
  if (!usesTabularConfiguration(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.tabularFieldsReducer.settings.showGroupedColumns;
};

export const selectSorts = (state: RootState) => {
  if (isPivotReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.pivotFieldsReducer.sorts;
  }

  if (usesTabularConfiguration(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.tabularFieldsReducer.sorts;
  }

  if (isChartReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.chartFieldsReducer.sorts;
  }

  if (isListReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.listFieldsReducer.sorts;
  }

  throw new Error("Report type not supported");
};

export const selectConditions = (state: RootState) => {
  const reportType = state.fieldsState.fieldsStateReducer.reportType;
  if (!reportType) {
    throw new Error("Report type not initialized");
  }

  if (isPivotReportType(reportType)) {
    return state.fieldsState.pivotFieldsReducer.conditions;
  }

  if (usesTabularConfiguration(reportType)) {
    return state.fieldsState.tabularFieldsReducer.conditions;
  }

  if (isChartReportType(reportType)) {
    return state.fieldsState.chartFieldsReducer.conditions;
  }

  if (isTileReportType(reportType)) {
    return state.fieldsState.tileFieldsReducer.conditions;
  }

  if (isListReportType(reportType)) {
    return state.fieldsState.listFieldsReducer.conditions;
  }

  if (isFinancialStatementReportType(reportType)) {
    return state.fieldsState.financialStatementFieldsReducer.conditions;
  }

  throw new Error("Report type not initialized");
};

export const selectValues = (state: RootState) => {
  if (
    usesTabularConfiguration(state.fieldsState.fieldsStateReducer.reportType) ||
    isTileReportType(state.fieldsState.fieldsStateReducer.reportType)
  ) {
    throw new Error("Not supported");
  }

  if (isPivotReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.pivotFieldsReducer.values;
  }

  if (isChartReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.chartFieldsReducer.values;
  }

  throw new Error("Report type not initialized");
};

export const selectRows = (state: RootState) => {
  if (!isPivotReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.pivotFieldsReducer.rows;
};

export const selectColumns = (state: RootState) => {
  if (!isPivotReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.pivotFieldsReducer.columns;
};

export const selectArguments = (state: RootState) => {
  if (!isChartReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.chartFieldsReducer.arguments;
};

export const selectFieldsStateConfiguration = createSelector(
  (state: RootState) => state.fieldsState.fieldsStateReducer.reportType,
  (state: RootState) => state.fieldsState.tabularFieldsReducer.configuration,
  (state: RootState) => state.fieldsState.pivotFieldsReducer.configuration,
  (state: RootState) => state.fieldsState.chartFieldsReducer.configuration,
  (state: RootState) => state.fieldsState.tileFieldsReducer.configuration,
  (state: RootState) => state.fieldsState.listFieldsReducer.configuration,
  (state: RootState) => state.fieldsState.financialStatementFieldsReducer.configuration,
  (reportType, tabularConfig, pivotConfig, chartConfig, tileConfig, listConfig, financialStatementConfig) => {
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (usesTabularConfiguration(reportType)) {
      return tabularConfig;
    }
    if (isPivotReportType(reportType)) {
      return pivotConfig;
    }
    if (isChartReportType(reportType)) {
      return chartConfig;
    }
    if (isTileReportType(reportType)) {
      return tileConfig;
    }
    if (isListReportType(reportType)) {
      return listConfig;
    }
    if (isFinancialStatementReportType(reportType)) {
      return financialStatementConfig;
    }
    throw new Error("Report type not supported");
  }
);

export const selectFieldsStateConfigurationSafe = (state: RootState) => {
  const reportType = state.fieldsState.fieldsStateReducer.reportType;
  if (!reportType) return undefined;
  return selectFieldsStateConfiguration(state);
};

export const selectBuilderDetailedView = (state: RootState) =>
  selectFieldsStateConfigurationSafe(state)?.settings.detailedView === true;

export const selectFieldsStateConfigurationHash = createSelector(selectFieldsStateConfiguration, (configuration) =>
  objectHash(configuration ?? {})
);

export const selectFieldsStateIsConfigurationEmpty = (state: RootState) => {
  const reportType = state.fieldsState.fieldsStateReducer.reportType;
  if (!reportType) {
    return true;
  }
  if (usesTabularConfiguration(reportType)) {
    return state.fieldsState.tabularFieldsReducer.isConfigurationEmpty;
  }
  if (isPivotReportType(reportType)) {
    return state.fieldsState.pivotFieldsReducer.isConfigurationEmpty;
  }
  if (isChartReportType(reportType)) {
    return state.fieldsState.chartFieldsReducer.isConfigurationEmpty;
  }
  if (isTileReportType(reportType)) {
    return state.fieldsState.tileFieldsReducer.isConfigurationEmpty;
  }
  if (isListReportType(reportType)) {
    return state.fieldsState.listFieldsReducer.isConfigurationEmpty;
  }
  if (isFinancialStatementReportType(reportType)) {
    return state.fieldsState.financialStatementFieldsReducer.isConfigurationEmpty;
  }
  throw new Error("Report type not supported");
};

const selectTabularDimensionNames = (state: RootState) => state.fieldsState.tabularFieldsReducer.dimensionNames;
const selectPivotDimensionNames = (state: RootState) => state.fieldsState.pivotFieldsReducer.dimensionNames;
const selectChartDimensionNames = (state: RootState) => state.fieldsState.chartFieldsReducer.dimensionNames;
const selectTabularMeasureNames = (state: RootState) => state.fieldsState.tabularFieldsReducer.measureNames;
const selectPivotMeasureNames = (state: RootState) => state.fieldsState.pivotFieldsReducer.measureNames;
const selectChartMeasureNames = (state: RootState) => state.fieldsState.chartFieldsReducer.measureNames;
const selectTileDimensionNames = (state: RootState) => state.fieldsState.tileFieldsReducer.dimensionNames;
const selectTileMeasureNames = (state: RootState) => state.fieldsState.tileFieldsReducer.measureNames;
const selectListDimensionNames = (state: RootState) => state.fieldsState.listFieldsReducer.dimensionNames;
const selectListMeasureNames = (state: RootState) => state.fieldsState.listFieldsReducer.measureNames;
const selectFinancialStatementDimensionNames = (state: RootState) =>
  state.fieldsState.financialStatementFieldsReducer.dimensionNames;
const selectFinancialStatementMeasureNames = (state: RootState) =>
  state.fieldsState.financialStatementFieldsReducer.measureNames;
export const selectListLedger = (state: RootState) => state.fieldsState.listFieldsReducer.ledger;

export const selectSelectedDimensionNames = createSelector(
  [
    selectReportType,
    selectTabularDimensionNames,
    selectPivotDimensionNames,
    selectChartDimensionNames,
    selectTileDimensionNames,
    selectListDimensionNames,
    selectFinancialStatementDimensionNames,
  ],
  (
    reportType,
    tabularDimensions,
    pivotDimensions,
    chartDimensions,
    tileDimensions,
    listDimensions,
    financialStatementDimensions
  ) => {
    if (!reportType) {
      return [];
    }
    if (usesTabularConfiguration(reportType)) {
      return tabularDimensions;
    }
    if (isPivotReportType(reportType)) {
      return pivotDimensions;
    }
    if (isChartReportType(reportType)) {
      return chartDimensions;
    }
    if (isTileReportType(reportType)) {
      return tileDimensions;
    }
    if (isListReportType(reportType)) {
      return listDimensions;
    }
    if (isFinancialStatementReportType(reportType)) {
      return financialStatementDimensions;
    }
    throw new Error("Report type not supported");
  }
);

export const selectSelectedMeasureNames = createSelector(
  [
    selectReportType,
    selectTabularMeasureNames,
    selectPivotMeasureNames,
    selectChartMeasureNames,
    selectTileMeasureNames,
    selectListMeasureNames,
    selectFinancialStatementMeasureNames,
  ],
  (
    reportType,
    tabularMeasures,
    pivotMeasures,
    chartMeasures,
    tileMeasures,
    listMeasures,
    financialStatementMeasures
  ) => {
    if (!reportType) {
      return [];
    }
    if (usesTabularConfiguration(reportType)) {
      return tabularMeasures;
    }
    if (isPivotReportType(reportType)) {
      return pivotMeasures;
    }
    if (isChartReportType(reportType)) {
      return chartMeasures;
    }
    if (isTileReportType(reportType)) {
      return tileMeasures;
    }
    if (isListReportType(reportType)) {
      return listMeasures;
    }
    if (isFinancialStatementReportType(reportType)) {
      return financialStatementMeasures;
    }
    throw new Error("Report type not supported");
  }
);
